import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { API_BASE, getAuthToken } from '../../api/agentApi';
import DetailItem from '../AgentDetail/DetailItem.tsx';

// Re-defining types and constants here, ideally these would be in a shared file
interface Tool {
  id: string;
  name: string;
  display_name: string;
  version: number; // Integer version (1, 2, 3, etc.)
  description: string;
  category: string | null;
  tags: string[];
  // code field removed - stored on disk
  // dependencies field removed
  parameters: Record<string, any>;
  returns: Record<string, any> | null;
  invocation_type: string;
  invocation_config: Record<string, any> | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  is_public: boolean;
  approved_by: string | null;
  approved_at: string | null;
  deprecation_reason: string | null;
  successor_version: number | null; // Integer version, not UUID
  usage_count: number;
}

const CATEGORIES = [
  { value: 'all', label: 'All Categories', icon: '📦' },
  { value: 'data_fetcher', label: 'Data Fetchers', icon: '📥' },
  { value: 'data_processor', label: 'Data Processors', icon: '⚙️' },
  { value: 'data_writer', label: 'Data Writers', icon: '📤' },
  { value: 'integration', label: 'Integrations', icon: '🔗' },
  { value: 'utility', label: 'Utilities', icon: '🛠️' },
  { value: 'ai_tool', label: 'AI Tools', icon: '🤖' },
];

const getCategoryIcon = (category: string | null) => {
  const cat = CATEGORIES.find((c) => c.value === category);
  return cat ? cat.icon : '📦';
};

const getCategoryLabel = (category: string | null) => {
  const cat = CATEGORIES.find((c) => c.value === category);
  return cat ? cat.label : category || 'Uncategorized';
};


const ToolDetailPage: React.FC = () => {
    const { toolId } = useParams<{ toolId: string }>();
    const navigate = useNavigate();
    const [tool, setTool] = useState<Tool | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tabValue, setTabValue] = useState(0);
    const [toolCode, setToolCode] = useState<string | null>(null);
    const [isLoadingCode, setIsLoadingCode] = useState(false);
    const [codeError, setCodeError] = useState<string | null>(null);

    useEffect(() => {
        const fetchTool = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const token = getAuthToken();
                const response = await fetch(`${API_BASE}/tools/${toolId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });
                if (!response.ok) {
                    throw new Error(`Failed to fetch tool: ${response.statusText}`);
                }
                const data = await response.json();
                setTool(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An unknown error occurred');
            } finally {
                setIsLoading(false);
            }
        };

        if (toolId) {
            fetchTool();
        }
    }, [toolId]);

    // Fetch tool code when Code tab is selected
    useEffect(() => {
        const fetchToolCode = async () => {
            if (tabValue !== 2 || !tool) return; // Only fetch when Code tab is active
            
            setIsLoadingCode(true);
            setCodeError(null);
            
            try {
                const token = getAuthToken();
                const response = await fetch(
                    `${API_BASE}/tools/${tool.id}/version/${tool.version}/code`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                        },
                    }
                );
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch tool code: ${response.statusText}`);
                }
                
                const data = await response.json();
                setToolCode(data.code || 'No code available');
            } catch (err) {
                setCodeError(err instanceof Error ? err.message : 'Failed to load code');
            } finally {
                setIsLoadingCode(false);
            }
        };

        fetchToolCode();
    }, [tabValue, tool]);

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!tool) {
        return <Alert severity="info">Tool not found.</Alert>;
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                    <Typography variant="h4" component="h1" fontWeight="bold">
                        {tool.display_name}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Version {tool.version}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tools')}>
                        Back to Catalog
                    </Button>
                    {/* <Button variant="contained" disabled>
                        Add to Agent
                    </Button> */}
                </Box>
            </Box>

            <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} aria-label="tool detail tabs">
                    <Tab sx={{ textTransform: 'none' }} label="Overview" />
                    <Tab sx={{ textTransform: 'none' }} label="Parameters" />
                    {/* <Tab sx={{ textTransform: 'none' }} label="Code" /> */}
                </Tabs>
            </Box>

            {/* Tab Content */}
            {tabValue === 0 && ( // Overview
                <Box>
                    <DetailItem label="Name" value={tool.display_name} />
                    <DetailItem label="Description" value={tool.description} />
                    <DetailItem label="Tool ID" value={tool.id} />
                    <DetailItem label="Category" value={<>{getCategoryIcon(tool.category)} {getCategoryLabel(tool.category)}</>} />
                    <DetailItem label="Usage Count" value={`${tool.usage_count} ${tool.usage_count === 1 ? 'agent' : 'agents'}`} />
                    <DetailItem 
                        label="Tags" 
                        value={
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {tool.tags.map((tag: string) => <Chip key={tag} label={tag} size="small" />)}
                            </Box>
                        }
                    />
                    <DetailItem label="Created By" value={tool.created_by} />
                    <DetailItem label="Created At" value={new Date(tool.created_at).toLocaleString()} />
                    <DetailItem label="Updated At" value={new Date(tool.updated_at).toLocaleString()} />
                    <DetailItem label="Invocation Type" value={tool.invocation_type} />
                    {tool.approved_by && (
                        <>
                            <DetailItem label="Approved By" value={tool.approved_by} />
                            <DetailItem label="Approved At" value={tool.approved_at ? new Date(tool.approved_at).toLocaleString() : 'N/A'} />
                        </>
                    )}
                    {tool.deprecation_reason && (
                        <DetailItem 
                            label="Deprecation Reason"
                            value={
                                <Alert severity="warning" sx={{ width: '100%' }}>
                                    {tool.deprecation_reason}
                                </Alert>
                            }
                        />
                    )}
                    {/* Dependencies removed - stored in tool.py file */}
                </Box>
            )}

            {tabValue === 1 && ( // Parameters
                <Box>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Input Parameters</Typography>
                    {(() => {
                        // Convert parameters to array format for display
                        // Parameters can be stored as JSON Schema (object with properties) or as array
                        let paramArray: any[] = [];
                        
                        if (Array.isArray(tool.parameters)) {
                            // Already in array format
                            paramArray = tool.parameters;
                        } else if (tool.parameters && typeof tool.parameters === 'object') {
                            if (tool.parameters.properties && typeof tool.parameters.properties === 'object') {
                                // JSON Schema format: convert properties object to array
                                paramArray = Object.entries(tool.parameters.properties).map(([name, prop]: [string, any]) => ({
                                    name,
                                    type: prop.type || 'string',
                                    description: prop.description,
                                    required: tool.parameters.required?.includes(name) || false,
                                    default: prop.default
                                }));
                            } else {
                                // Flat object format: convert each key-value to parameter
                                paramArray = Object.entries(tool.parameters).map(([name, value]: [string, any]) => {
                                    if (typeof value === 'object' && value !== null) {
                                        return {
                                            name,
                                            type: value.type || 'string',
                                            description: value.description,
                                            required: value.required || false,
                                            default: value.default
                                        };
                                    }
                                    return { name, type: 'string', description: String(value) };
                                });
                            }
                        }
                        
                        return paramArray.length > 0 ? (
                            <Box sx={{ mb: 3 }}>
                                {paramArray.map((param: any, idx: number) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            mb: 2,
                                            p: 2,
                                            borderLeft: '3px solid',
                                            borderColor: 'primary.main',
                                            bgcolor: 'grey.50',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: '150px' }}>
                                                {param.name}
                                            </Typography>
                                            <Chip 
                                                label={param.type || 'string'} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined"
                                                sx={{ fontFamily: 'monospace' }}
                                            />
                                            {param.required && (
                                                <Chip 
                                                    label="Required" 
                                                    size="small" 
                                                    color="error" 
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                        {param.description && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                {param.description}
                                            </Typography>
                                        )}
                                        {param.default !== undefined && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                Default: <code style={{ padding: '2px 6px', backgroundColor: '#e0e0e0', borderRadius: '4px' }}>{JSON.stringify(param.default)}</code>
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Alert severity="info" sx={{ mb: 3 }}>
                                No input parameters defined for this tool.
                            </Alert>
                        );
                    })()}

                    {tool.returns && (
                        <>
                            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>Return Value</Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    borderLeft: '3px solid',
                                    borderColor: 'success.main',
                                    bgcolor: 'grey.50',
                                    borderRadius: 1,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Return Type
                                    </Typography>
                                    <Chip 
                                        label={tool.returns.type || 'any'} 
                                        size="small" 
                                        color="success" 
                                        variant="outlined"
                                        sx={{ fontFamily: 'monospace' }}
                                    />
                                </Box>
                                {tool.returns.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {tool.returns.description}
                                    </Typography>
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            )}

            {/* {tabValue === 2 && ( // Code
                <Box>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Tool Implementation</Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                        Code location: <code>.tools/{tool.id}/{tool.version}/{tool.name}.py</code>
                    </Typography>
                    
                    {isLoadingCode ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : codeError ? (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {codeError}
                        </Alert>
                    ) : toolCode ? (
                        <Box
                            sx={{
                                bgcolor: '#000000',
                                color: '#ffffff',
                                p: 2,
                                borderRadius: 1,
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                overflowX: 'auto',
                                maxHeight: '600px',
                                overflowY: 'auto',
                                '& pre': {
                                    margin: 0,
                                    whiteSpace: 'pre',
                                },
                            }}
                        >
                            <pre>{toolCode}</pre>
                        </Box>
                    ) : (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            No code available for this tool.
                        </Alert>
                    )}
                </Box>
            )} */}

        </Box>
    );
};

export default ToolDetailPage;